/**
 * Trip-level text contributed by an imported itinerary document.
 *
 * An itinerary booklet carries text that belongs to no single place: an
 * introduction to the trip, and topical sections such as insurance, currency or
 * tipping. Both used to be discarded — the ride was created with an empty
 * description, and the only path for a note required a date.
 *
 * Kept apart from the Vuex module so the merge rules can be read and tested on
 * their own; the store holds the result and posts it once the ride exists.
 */

function emptyTripInfo() {
  return { rideSummary: '', generalInfo: [] };
}

function asSection(title, text) {
  const cleanTitle = (title || '').trim();
  const cleanText = (text || '').trim();
  if (!cleanTitle && !cleanText) return null;
  return { title: cleanTitle, text: cleanText };
}

/**
 * Fold one document's trip-level text into what the ride has collected so far.
 *
 * The introduction is first-wins, matching how the ride name already behaves: a
 * voucher imported after the booklet must not overwrite the booklet's opening.
 * Titled sections accumulate instead, because two documents genuinely can each
 * carry information the traveller needs.
 *
 * A note that arrived without a date is promoted to a titled section rather than
 * dropped — the dated path cannot store it, and it is usually the trip-wide
 * advice that matters most. A note that HAS a date is left untouched here, so it
 * still reaches its own day and is not written twice.
 *
 * Returns a new object; the state passed in is never modified.
 */
function mergeTripInfo(current, data) {
  const state = current || emptyTripInfo();
  const document = data || {};

  const sections = [];
  (document.general_info || []).forEach((section) => {
    const kept = asSection(section && section.title, section && section.text);
    if (kept) sections.push(kept);
  });
  (document.day_notes || []).forEach((note) => {
    // A note the document tied to a day -- by date or by day number -- belongs to
    // that day, not to the trip as a whole.
    if (note && (note.date || note.day_number)) return;
    const kept = asSection(note && note.title, note && note.note);
    if (kept) sections.push(kept);
  });

  const summary = (document.ride_summary || '').trim();

  return {
    rideSummary: state.rideSummary || summary,
    generalInfo: state.generalInfo.concat(sections),
  };
}

module.exports = { emptyTripInfo, mergeTripInfo };
